import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { fmtAMD } from '@/lib/format';
import type { MoctEvent, TicketTier } from '@/types';

import styles from './EventTicketRail.module.css';

interface EventTicketRailProps {
  event: MoctEvent;
}

function isSoldOut(tier: TicketTier): boolean {
  return tier.remaining === 0;
}

export default function EventTicketRail({ event }: EventTicketRailProps) {
  const nav = useNavigate();
  if (event.tiers.length === 0) return null;

  const goTier = (tier: TicketTier) => {
    if (!isSoldOut(tier)) nav(`/tickets/${event.id}?tier=${tier.id}`);
  };

  const goAll = () => nav(`/tickets/${event.id}`);

  const onKeyActivate =
    (run: () => void) =>
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        run();
      }
    };

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <span>TICKETS</span>
        <span className={styles.headerLine} />
      </div>
      <ol className={styles.list}>
        {event.tiers.map((tier) => {
          const soldOut = isSoldOut(tier);
          return (
            <li
              key={tier.id}
              className={styles.row}
              data-soldout={soldOut ? 'true' : 'false'}
              onClick={() => goTier(tier)}
              onKeyDown={onKeyActivate(() => goTier(tier))}
              role={soldOut ? undefined : 'button'}
              tabIndex={soldOut ? -1 : 0}
            >
              <div className={styles.mid}>
                <span className={styles.name}>{tier.name}</span>
              </div>
              <div className={styles.rt}>
                {soldOut ? (
                  <span className={styles.soldStamp}>SOLD OUT</span>
                ) : (
                  <>
                    <span className={styles.price}>{fmtAMD(tier.price)}</span>
                    {tier.note ? <span className={styles.next}>{tier.note}</span> : null}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <div
        className={styles.cta}
        onClick={goAll}
        onKeyDown={onKeyActivate(goAll)}
        role="button"
        tabIndex={0}
      >
        <span>GET TICKETS</span>
        <span className={styles.ctaArrow}>→</span>
      </div>
    </section>
  );
}
